import { HTMLAttributes, useMemo } from 'react';

import { Box, Stack, styled, Typography } from '@mui/material';

import { List, ListItem } from '../../lists';
import Scroller from '../../scroller/Scroller';
import { tokens } from '../../../theme/generated/tokens';
import { Criterion, RecentSearchEntry } from '../types';
import { mapRecentSearchesToListItems } from '../utils';

const Root = styled('div', {
  name: 'DsSearch',
  slot: 'RecentSearch',
  target: 'DsSearch-RecentSearch',
  overridesResolver: (_, styles) => {
    return [styles.recentSearch];
  },
})<HTMLAttributes<HTMLDivElement>>``;

const Label = styled('div', {
  name: 'DsSearch',
  slot: 'RecentSearchLabel',
  target: 'DsSearch-RecentSearchLabel',
  overridesResolver: (_, styles) => {
    return [styles.recentSearchLabel];
  },
})<HTMLAttributes<HTMLDivElement>>``;

const StyledList = styled(List, {
  name: 'DsSearch',
  slot: 'RecentSearch',
  target: 'DsSearch-RecentSearchList',
})<HTMLAttributes<HTMLDivElement>>``;

export type RecentSearchProps = {
  criteria?: Criterion[];
  recentSearches?: RecentSearchEntry[];
  onSelect?: (index: number) => void;
};

export const RecentSearch = ({
  criteria,
  recentSearches,
  onSelect,
}: RecentSearchProps) => {
  const searches = useMemo(
    () =>
      mapRecentSearchesToListItems({
        criteria,
        recentSearches,
      }),
    [criteria, recentSearches]
  );

  if (!recentSearches?.length || !searches.some((s) => !!s.length)) {
    return null;
  }

  return (
    <Root data-testid="recent-options-list">
      <Label>Recent search</Label>
      <Scroller>
        <StyledList disablePadding>
          {searches.map((entry, index) => {
            return (
              <ListItem
                key={index}
                testId={`recent-option-${index}`}
                onClick={() => {
                  onSelect?.(index);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                {entry.map(({ title, value }, index) => {
                  const notLast = index < entry.length - 1;
                  return (
                    <Stack
                      key={index}
                      gap="4px"
                      direction="row"
                      flexWrap="wrap"
                    >
                      {title && (
                        <Typography
                          variant="P14M"
                          sx={{ color: tokens.theme.fg.structural.primary }}
                        >
                          {title}
                        </Typography>
                      )}
                      {value && Array.isArray(value) ? (
                        value.map((v, i) => {
                          return (
                            <Typography
                              key={i}
                              variant="P14R"
                              display="inline-flex"
                              alignItems="center"
                              sx={{
                                color: tokens.theme.fg.structural.secondary,
                              }}
                            >
                              {v}
                              {value.length - 1 !== i ? (
                                <Box component="span" display="inline-block">
                                  ,
                                </Box>
                              ) : null}
                            </Typography>
                          );
                        })
                      ) : (
                        <Typography
                          variant="P14R"
                          sx={{ color: tokens.theme.fg.structural.secondary }}
                        >
                          {value}
                          {notLast && ','}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </ListItem>
            );
          })}
        </StyledList>
      </Scroller>
    </Root>
  );
};
